import React, { useState, useEffect, useRef, useCallback } from "react";
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { Card, CardHeader } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSchedules, SchedulesQueryOptions, Schedule } from "@/pages/scheduling/hooks/useSchedules";
import { useGuards } from "@/pages/scheduling/hooks/useGuards";
import { useCalendarNavigation } from "@/pages/scheduling/hooks/useCalendarNavigation";
import { CalendarViewMode, CalendarEvent, CalendarPermissions } from "@/pages/scheduling/types";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useManagerPermissions } from "@/hooks/useManagerPermissions";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useTeams } from "@/components/hr/hooks/useTeams";
import AddScheduleDialog from "./AddScheduleDialog";
import QuickEditDialog from "./QuickEditDialog";
import DeleteShiftConfirmationDialog from "./DeleteShiftConfirmationDialog";
import CalendarAddButton from "./CalendarAddButton";
import BulkUploadShifts from "./BulkUploadShifts";
import CalendarHeader from "./CalendarHeader";
import CalendarToolbar from "./CalendarToolbar";
import CalendarViewSwitcher from "./CalendarViewSwitcher";
import WeekVisibilityToggle from "./WeekVisibilityToggle";
import { useShiftCopy } from '../hooks/useShiftCopy';
import { useManualSwap } from '../hooks/useManualSwap';
import { useCalendarEvents } from "../hooks/useCalendarEvents";
import { useTotalWorkView } from "../hooks/useTotalWorkView";
import { useExportImport } from "../hooks/useExportImport";
import { useWeekVisibility } from "../hooks/useWeekVisibility";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, format, isWithinInterval, addDays, getDay, subDays, addMonths, subMonths } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import TemplatesDialog from "./TemplatesDialog";
import PasteOptionsDialog from "./PasteOptionsDialog";
import CopyConfirmDialog from "./CopyConfirmDialog";
import PasteConfirmDialog from "./PasteConfirmDialog";
import { useMultiSelect } from "../hooks/useMultiSelect";
import { useSchedulePermissions } from "../hooks/useSchedulePermissions";
import { useScheduleState } from "../hooks/useScheduleState";
import { useScheduleOperations } from "../hooks/useScheduleOperations";
import { useShiftApiWithUndo } from "../hooks/useShiftApiWithUndo";

interface ScheduleCalendarProps {
  teamId: string;
  onDateRangeChange?: (range: { start: Date, end: Date }) => void;
  onClear?: (type: 'week' | 'month' | 'custom') => void;
  calendarZoom?: number;
}

declare global {
  interface Window {
    guards: any[] | null;
  }
}

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({ teamId, onDateRangeChange, onClear, calendarZoom = 0.9 }) => {
  const { t } = useLanguage();
  const { user } = useSupabaseUser();
  const { teams } = useTeams();
  const { hasAdminLikePermissions, permissions, isLoadingManagerPermissions } = useSchedulePermissions(teamId);
  const {
    isCustomView,
    setIsCustomView,
    selectedGuardId,
    setSelectedGuardId,
    isTemplatesDialogOpen,
    setIsTemplatesDialogOpen,
    showQuickEditDialog,
    setShowQuickEditDialog,
    quickEditEvent,
    setQuickEditEvent,
    viewType,
    setViewType,
    customClearRange,
    setCustomClearRange,
    isLoadingMoreShifts,
    setIsLoadingMoreShifts,
    extendedDateRange,
    setExtendedDateRange
  } = useScheduleState();
  
  const isMobile = useIsMobile();

  console.log('ScheduleCalendar permissions check:', {
    hasAdminLikePermissions,
    isLoadingManagerPermissions,
    teamId,
    userId: user?.id
  });

  const {
    viewMode,
    currentDate,
    dateRange,
    isCalendarOpen,
    customDateRange,
    handleViewModeChange,
    navigatePrevious,
    navigateNext,
    navigatePreviousDay,
    navigateNextDay,
    goToToday,
    setIsCalendarOpen,
    handleCalendarSelect,
    handleDateRangeSelect,
    getDateRange,
    setDateRange,
    currentViewDates
  } = useCalendarNavigation();

  const getScheduleQueryOptions = useCallback((): SchedulesQueryOptions => {
    const currentDateRange = getDateRange();
    
    if (extendedDateRange && currentDateRange) {
      const { start, end } = currentDateRange;
      
      let extendedStart, extendedEnd;

      if (viewMode === 'month') {
        extendedStart = subDays(start, 30);
        extendedEnd = addDays(end, 30); 
      } else {
        extendedStart = subDays(start, 15);
        extendedEnd = addDays(end, 15);
      }

      return {
        startDate: extendedStart.toISOString(),
        endDate: extendedEnd.toISOString(),
        extendedRange: true
      };
    }
    
    return {
      startDate: currentDateRange?.start.toISOString(),
      endDate: currentDateRange?.end.toISOString(),
      extendedRange: false
    };
  }, [extendedDateRange, getDateRange, viewMode]);

  const queryOptions = getScheduleQueryOptions();

  const { data: schedules, isLoading, refetch } = useSchedules(teamId, queryOptions);
  const { availableGuards: guards, allGuards, isLoading: isLoadingGuards } = useGuards(teamId);
  
  const [guardsVersion, setGuardsVersion] = useState(0);
  
  useEffect(() => {
    if (allGuards) {
      window.guards = allGuards; // Use allGuards (including archived) for display
      // Increment version to trigger re-renders of child components
      setGuardsVersion(prev => prev + 1);
    }
  }, [allGuards]);
  
  // Listen for guard updates to force re-render
  useEffect(() => {
    const handleGuardUpdate = () => {
      console.log('Guard data updated, forcing calendar re-render');
      setGuardsVersion(prev => prev + 1);
    };

    document.addEventListener('refetch-schedules', handleGuardUpdate);

    return () => {
      document.removeEventListener('refetch-schedules', handleGuardUpdate);
    };
  }, []);
  
  const {
    isMultiSelectMode,
    toggleMultiSelectMode,
    selectedEvents,
    toggleEventSelection,
    clearSelection,
    getSelectedEvents,
    selectedCount,
    selectAll,
    deselectAll,
    isSelected,
    deleteSelectedEvents
  } = useMultiSelect();
  
  const {
    isLoading: isLoadingVisibility,
    isDateVisible,
    updateWeekVisibility,
    loadWeekVisibility,
    hasAdminLikePermissions: hasWeekVisibilityPermissions
  } = useWeekVisibility(teamId, currentDate);
  
  const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const isCurrentWeekVisible = isDateVisible(currentWeekStart);
  
  useEffect(() => {
    loadWeekVisibility(currentDate);
  }, [currentDate, loadWeekVisibility]);
  
  useEffect(() => {
    const currentDateRange = getDateRange();
    if (onDateRangeChange && currentDateRange) {
      onDateRangeChange(currentDateRange);
    }
  }, [currentDate, viewMode, customDateRange, getDateRange, onDateRangeChange]);
  
  console.log('ScheduleCalendar rendering with:', { 
    teamId, 
    schedulesCount: schedules?.length,
    guardsCount: guards?.length,
    hasAdminLikePermissions,
    viewMode: viewMode,
    isMobile: isMobile,
    isCurrentWeekVisible,
    extendedDateRange
  });

  const getFilteredSchedules = () => {
    if (!schedules) return [];
    
    console.log('getFilteredSchedules - Input:', {
      schedulesCount: schedules.length,
      hasAdminLikePermissions,
      isCurrentWeekVisible,
      selectedGuardId,
      currentWeekStart: currentWeekStart.toISOString()
    });
    
    // For users with admin-like permissions, show all schedules
    if (hasAdminLikePermissions) {
      let filtered = [...schedules];
      
      console.log('getFilteredSchedules - Admin-like permissions, showing all schedules WITHOUT week visibility check');
      
      // Only apply guard filter if a specific guard is selected
      if (selectedGuardId && selectedGuardId !== 'all') {
        filtered = filtered.filter(schedule =>
          schedule.assigned_guards &&
          Array.isArray(schedule.assigned_guards) &&
          schedule.assigned_guards.includes(selectedGuardId)
        );
        console.log('getFilteredSchedules - Filtered by guard:', {
          selectedGuardId,
          filteredCount: filtered.length
        });
      }
      
      console.log('getFilteredSchedules - Final result for admin-like user:', {
        count: filtered.length
      });
      
      return filtered;
    }

    // For regular guards, check week visibility first
    if (!isCurrentWeekVisible) {
      console.log('getFilteredSchedules - Week not visible to regular guards, filtering out all schedules');
      return [];
    }

    // For regular guards, the database query already filtered by assigned guards
    console.log('getFilteredSchedules - Final result for regular guard:', {
      count: schedules.length
    });

    return schedules;
  };
  
  const filteredSchedules = getFilteredSchedules();
  
  // Undo/Redo functionality (team-specific, clears on team change) - must be before useCalendarEvents
  const {
    createShift,
    updateShift,
    deleteShift,
    deleteBulkShifts,
    undo,
    redo,
    canUndo,
    canRedo
  } = useShiftApiWithUndo(refetch as any, teamId);
  
  const calendarEvents = useCalendarEvents(teamId, permissions, refetch as any, deleteShift);
  const {
    showAddDialog,
    editEvent,
    selectedDate,
    isClearing,
    showDeleteDialog,
    shiftToDelete,
    isDeleting,
    handleAddEvent,
    handleDialogClose,
    handleDayClick,
    handleEventAdded,
    handleEventClick,
    handleDuplicateEvent,
    handleDeleteEvent,
    handleConfirmDeleteShift,
    handleCancelDeleteShift,
    handleClearCalendar,
    handleDragEnd
  } = calendarEvents;

  // Quick edit handler
  const handleQuickEdit = useCallback((event: CalendarEvent) => {
    setQuickEditEvent(event);
    setShowQuickEditDialog(true);
  }, [setQuickEditEvent, setShowQuickEditDialog]);

  // Enhanced event click that supports quick edit
  const handleEventClickWithQuickEdit = useCallback((event: CalendarEvent, e: React.MouseEvent, isMultiSelectMode?: boolean) => {
    handleEventClick(event, e, isMultiSelectMode, handleQuickEdit);
  }, [handleEventClick, handleQuickEdit]);

  const {
    guardSchedules,
    allGuardsSchedules,
    isLoadingGuardSchedules,
    loadAllGuardsSchedules,
    loadGuardSchedules
  } = useTotalWorkView(teamId, guards, getDateRange());

  const {
    isExporting,
    isBulkUploadOpen,
    handleBulkUpload,
    closeBulkUpload,
    handleBulkUploadSuccess,
    handleExportData
  } = useExportImport(teamId, getDateRange, refetch as any);

  const { copyShifts, pasteShifts, clearCopiedShifts, hasCopiedShifts, withGuards, copyMode, copySourceDate } = useShiftCopy();
  const { markedShiftForSwap, markShiftForSwap, clearMarkedShift, swapShifts } = useManualSwap();
  
  const { handleClearOption, handleApplyTemplate } = useScheduleOperations(teamId, user, refetch as any);

  const handleViewModeMenuSelect = (mode: CalendarViewMode) => {
    handleViewModeChange(mode);
    
    setExtendedDateRange(false);
    
    if (mode === 'total-work') {
      if (hasAdminLikePermissions && !selectedGuardId) {
        loadAllGuardsSchedules();
      } 
      else if (guards && guards.length > 0) {
        const targetGuard = hasAdminLikePermissions ? guards[0] : guards.find(g => g.id === user?.id);
        if (targetGuard) {
          handleGuardSelect(targetGuard.id);
        }
      }
    } else {
      handleGuardSelect('');
    }
  };

  const handleLoadMoreShifts = async () => {
    if (viewMode === 'total-work') {
      return;
    }
    
    try {
      setIsLoadingMoreShifts(true);
      setExtendedDateRange(true);
      
      toast.success(t("loadingMoreShifts") || "Loading more shifts...");
    } catch (error) {
      console.error('Error loading more shifts:', error);
      toast.error(t("errorLoadingMoreShifts") || "Error loading more shifts");
    } finally {
      setTimeout(() => {
        setIsLoadingMoreShifts(false);
        toast.success(
          t("additionalShiftsLoaded") || "Additional shifts loaded", 
          { description: t("shiftsLoadedFromExtendedRange") || "Shifts loaded from extended date range" }
        );
      }, 1000);
    }
  };

  const handleToggleCustomView = () => {
    setIsCustomView(prev => !prev);
  };

  const handleCopyWeek = (events: CalendarEvent[], withGuards: boolean = false) => {
    console.log('ScheduleCalendar: handleCopyWeek START');
    console.log('ScheduleCalendar: events length:', events.length);
    console.log('ScheduleCalendar: withGuards:', withGuards);
    
    // Use the events as they are - they're already filtered for display
    // Only filter by current week date range since events might span multiple weeks
    const currentWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const currentWeekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    
    console.log('Copy week debug:', {
      currentDate: currentDate.toISOString(),
      currentWeekStart: currentWeekStart.toISOString(),
      currentWeekEnd: currentWeekEnd.toISOString(),
      teamId,
      totalEvents: events.length
    });
    
    // Filter to only include shifts that start within the current week
    const filteredEvents = events.filter(event => {
      const eventStartDate = fromLocalISOString(event.start_date);
      const isInCurrentWeek = eventStartDate >= currentWeekStart && eventStartDate <= currentWeekEnd;
      
      console.log('Event filter check:', {
        eventId: event.id,
        eventStartDate: eventStartDate.toISOString(),
        isInCurrentWeek,
        title: event.title
      });
      
      return isInCurrentWeek;
    });

    console.log('Filtered events for copy:', {
      filteredCount: filteredEvents.length,
      events: filteredEvents.map(e => ({
        id: e.id,
        start_date: e.start_date,
        end_date: e.end_date,
        title: e.title
      }))
    });
    
    if (filteredEvents.length === 0) {
      toast.info(t("noShiftsToCopy") || "No shifts to copy for the current week");
      return;
    }
    
    // Create a stable callback to avoid re-renders - but create it inline to avoid dependency issues
    const handleConfirmCopy = () => {
      console.log('ScheduleCalendar: inline handleConfirmCopy START');
      copyShifts(filteredEvents, withGuards, 'week');
      console.log('ScheduleCalendar: inline handleConfirmCopy END');
    };
    
    // Show confirmation dialog
    console.log('ScheduleCalendar: About to set copy confirm dialog');
    
    setCopyConfirm({
      isOpen: true,
      shiftsCount: filteredEvents.length,
      withGuards,
      copyMode: 'week',
      sourceDate: currentDate,
      onConfirm: handleConfirmCopy
    });
    
    console.log('ScheduleCalendar: handleCopyWeek END');
  };

  const handleCopyCustomRange = (events: CalendarEvent[], withGuards: boolean = false, mode: 'week' | 'custom' | 'month' = 'week') => {
    // Filter events for the current team only
    const filteredEvents = events.filter(event => event.team_id === teamId);
    
    if (filteredEvents.length === 0) {
      toast.info(t("noShiftsToCopy") || "No shifts to copy for the current team");
      return;
    }
    
    // Create a stable callback to avoid re-renders
    const handleConfirmCopy = () => {
      copyShifts(filteredEvents, withGuards, mode);
      toast.success(t("shiftsCopied") || `${filteredEvents.length} shifts copied successfully`);
    };
    
    // Show confirmation dialog
    setCopyConfirm({
      isOpen: true,
      shiftsCount: filteredEvents.length,
      withGuards,
      copyMode: mode,
      sourceDate: copySourceDate || currentDate,
      onConfirm: handleConfirmCopy
    });
  };

  const handleGuardSelect = (guardId: string) => {
    setSelectedGuardId(guardId);
    
    if (guardId === 'all') {
      loadAllGuardsSchedules();
    } else if (guardId) {
      loadGuardSchedules(guardId);
    }
  };

  const [pasteOptions, setPasteOptions] = useState<{
    isOpen: boolean;
    targetDate: Date | null;
    shiftsCount: number;
    copiedWithGuards?: boolean;
    copyMode?: 'week' | 'custom' | 'month';
  }>({
    isOpen: false,
    targetDate: null,
    shiftsCount: 0
  });

  const [copyConfirm, setCopyConfirm] = useState<{
    isOpen: boolean;
    shiftsCount: number;
    withGuards: boolean;
    copyMode: 'week' | 'custom' | 'month';
    sourceDate: Date;
    onConfirm: () => void;
  } | null>(null);

  const [pasteConfirm, setPasteConfirm] = useState<{
    isOpen: boolean;
    shiftsCount: number;
    targetDate: Date;
    sourceDate: Date;
    copyMode: 'week' | 'custom' | 'month';
    withGuards: boolean;
    includeGuards: boolean;
    onConfirm: () => void;
  } | null>(null);

  const handleShowPasteOptions = useCallback((targetDate: Date) => {
    console.log('ScheduleCalendar: handleShowPasteOptions START');
    
    if (!hasCopiedShifts) {
      toast.error(t("nothingToPaste") || "Nothing to paste");
      return;
    }

    const shiftsToCreate = pasteShifts(targetDate);
    
    if (shiftsToCreate.length === 0) {
      toast.error(t("nothingToPaste") || "Nothing to paste");
      return;
    }
    
    // Show paste options dialog directly without complex state dependencies
    setPasteOptions({
      isOpen: true,
      targetDate,
      shiftsCount: shiftsToCreate.length,
      copiedWithGuards: withGuards,
      copyMode: copyMode
    });
    
  }, [hasCopiedShifts, pasteShifts, withGuards, copyMode, t]);

  const handlePasteWithOptions = useCallback(async (targetDate: Date, includeGuards: boolean) => {
    console.log('ScheduleCalendar: handlePasteWithOptions - Direct execution');
    
    // Close dialogs immediately to prevent state loops
    setPasteOptions(prev => ({ ...prev, isOpen: false }));
    setPasteConfirm(null);
    
    // Execute paste directly without additional confirmation dialog
    if (!teamId || !user) {
      console.log('Missing teamId or user');
      return;
    }
    
    try {
      const shiftsToCreate = pasteShifts(targetDate, includeGuards);
      
      if (shiftsToCreate.length === 0) {
        toast.error(t("nothingToPaste") || "Nothing to paste");
        return;
      }
      
      const shiftsWithMetadata = shiftsToCreate.map(shift => ({
        ...shift,
        team_id: teamId,
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));
      
      const loadingToastId = toast.loading(t("pastingShifts") || "Pasting shifts...");
      
      const { data, error } = await supabase
        .from('team_schedules')
        .insert(shiftsWithMetadata)
        .select();
      
      toast.dismiss(loadingToastId);
      
      if (error) {
        console.error('Error pasting shifts:', error);
        toast.error(t("errorPastingShifts") || "Error pasting shifts");
        return;
      }
      
      const insertedCount = data ? data.length : shiftsWithMetadata.length;
      toast.success(t("shiftsPasted") || `${insertedCount} shifts pasted successfully`);
      
      // Clear copied shifts and refresh
      clearCopiedShifts();
      setTimeout(() => refetch(), 300);
      
    } catch (error) {
      console.error('Error pasting shifts:', error);
      toast.error(t("errorPastingShifts") || "Error pasting shifts");
    }
  }, [teamId, user, pasteShifts, supabase, clearCopiedShifts, refetch, t]);

  const handlePasteWeek = async (targetDate: Date, includeGuards?: boolean) => {
    console.log('ScheduleCalendar: handlePasteWeek START');
    console.log('ScheduleCalendar: targetDate:', targetDate);
    console.log('ScheduleCalendar: includeGuards:', includeGuards);
    console.log('ScheduleCalendar: teamId:', teamId);
    console.log('ScheduleCalendar: user:', user ? 'present' : 'null');
    
    if (!teamId || !user) {
      console.log('ScheduleCalendar: Missing teamId or user, returning early');
      return;
    }
    
    try {
      console.log('ScheduleCalendar: Calling pasteShifts...');
      const shiftsToCreate = pasteShifts(targetDate, includeGuards);
      console.log('ScheduleCalendar: shiftsToCreate length:', shiftsToCreate.length);
      
      if (shiftsToCreate.length === 0) {
        console.log('ScheduleCalendar: No shifts to create');
        toast.error(t("nothingToPaste") || "Nothing to paste");
        return;
      }
      
      if (shiftsToCreate.length > 500) {
        console.log('ScheduleCalendar: Too many shifts');
        toast.error(t("tooManyShifts") || "Too many shifts to paste at once (max 500)");
        return;
      }
      
      console.log('ScheduleCalendar: About to create shifts in database');
      console.log(`Attempting to paste ${shiftsToCreate.length} shifts`);
      
      const shiftsWithMetadata = shiftsToCreate.map(shift => ({
        ...shift,
        team_id: teamId,
        created_by: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }));
      
      console.log('ScheduleCalendar: shiftsWithMetadata prepared:', shiftsWithMetadata.length);
      
      const loadingToastId = toast.loading(t("pastingShifts") || "Pasting shifts...");
      console.log('ScheduleCalendar: Loading toast shown');
      
      console.log('ScheduleCalendar: About to call supabase insert');
      const { data, error, count } = await supabase
        .from('team_schedules')
        .insert(shiftsWithMetadata)
        .select();
      
      console.log('ScheduleCalendar: Supabase insert completed');
      console.log('ScheduleCalendar: data:', data ? data.length : 'null');
      console.log('ScheduleCalendar: error:', error);
      console.log('ScheduleCalendar: count:', count);
      
      toast.dismiss(loadingToastId);
      console.log('ScheduleCalendar: Loading toast dismissed');
      
      if (error) {
        console.error('ScheduleCalendar: Error pasting shifts:', error);
        toast.error(t("errorPastingShifts") || "Error pasting shifts");
        return;
      }
      
      const insertedCount = count || (data ? data.length : shiftsWithMetadata.length);
      console.log('ScheduleCalendar: insertedCount:', insertedCount);
      
      const modeText = copyMode === 'week' ? 'week' : copyMode === 'month' ? 'month' : 'selected range';
      const guardsText = includeGuards !== undefined 
        ? (includeGuards ? "with guards" : "without guards")
        : (withGuards ? "with guards" : "without guards");
      
      console.log('ScheduleCalendar: About to show success toast');
      
      toast.success(
        t("shiftsPasted") || `${insertedCount} shifts pasted successfully from ${modeText}`,
        { 
          description: t(guardsText) || guardsText,
          duration: 3000
        }
      );
      
      console.log('ScheduleCalendar: Success toast shown, about to refetch');
      
      setTimeout(() => {
        console.log('ScheduleCalendar: Calling refetch');
        refetch();
        console.log('ScheduleCalendar: Refetch called');
      }, 300);
      
      console.log('ScheduleCalendar: handlePasteWeek SUCCESS END');
    } catch (error) {
      console.error('ScheduleCalendar: Error pasting shifts:', error);
      toast.error(t("errorPastingShifts") || "Error pasting shifts");
    }
    
    console.log('ScheduleCalendar: handlePasteWeek END');
  };

  // Simplified paste options callback - no complex dependencies
  const handlePasteOptionsConfirm = useCallback((includeGuards: boolean) => {
    if (pasteOptions.targetDate) {
      handlePasteWithOptions(pasteOptions.targetDate, includeGuards);
    }
  }, [pasteOptions.targetDate, handlePasteWithOptions]);

  // Memoize the paste options close callback  
  const handlePasteOptionsClose = useCallback(() => {
    setPasteOptions(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Remove the old handlePasteWeek function since we're handling it directly in handlePasteWithOptions

  const currentDateRange = getDateRange();
  
  const handleDateRangeSelectAdapter = (start: Date, end: Date) => {
    handleDateRangeSelect({ from: start, to: end });
  };
  
  const handleWeekVisibilityChange = async (date: Date, isVisible: boolean) => {
    const success = await updateWeekVisibility(date, isVisible);
    
    if (success) {
      refetch();
    }
    
    return success;
  };
  
  const CustomDateRangePicker = () => {
    return (
      <div className="flex items-center space-x-2 mt-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-full justify-start text-left font-normal",
                !customDateRange?.from && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {customDateRange?.from ? (
                customDateRange.to ? (
                  <>
                    {format(customDateRange.from, "LLL dd, y")} - {format(customDateRange.to, "LLL dd, y")}
                  </>
                ) : (
                  format(customDateRange.from, "LLL dd, y")
                )
              ) : (
                <span>{t('selectRange') || "Select date range"}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={currentDate}
              selected={customDateRange}
              onSelect={handleDateRangeSelect}
              numberOfMonths={2}
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  };
  
  const handleTemplatesClick = () => {
    setIsTemplatesDialogOpen(true);
  };
  
  const handleViewTypeChange = (type: 'standard' | 'per-job') => {
    setViewType(type);
  };
  
  return (
    <div className="relative z-10">
      <Card className="relative z-10 shadow-md">
        <CardHeader className="pb-3">
          <CalendarToolbar
            isAdmin={hasAdminLikePermissions}
            hasAdminLikePermissions={hasAdminLikePermissions}
            viewMode={viewMode}
            currentDate={currentDate}
            onAddEvent={handleAddEvent}
            onExport={handleExportData}
            onBulkUpload={handleBulkUpload}
            onNavigatePrevious={navigatePrevious}
            onNavigateNext={navigateNext}
            onNavigatePreviousDay={navigatePreviousDay}
            onNavigateNextDay={navigateNextDay}
            onGoToToday={goToToday}
            onViewModeChange={handleViewModeMenuSelect}
            isExporting={isExporting}
            guards={guards}
            selectedGuardId={selectedGuardId}
            handleGuardSelect={handleGuardSelect}
            onTemplatesClick={handleTemplatesClick}
            onLoadMoreShifts={handleLoadMoreShifts}
            isLoadingMoreShifts={isLoadingMoreShifts}
            viewType={viewType}
            onViewTypeChange={handleViewTypeChange}
            onUndo={undo}
            onRedo={redo}
            canUndo={canUndo}
            canRedo={canRedo}
          />
          
          {hasAdminLikePermissions && (
            <CalendarHeader
              currentDate={currentDate}
              onCopyWeek={() => handleCopyWeek(
                (filteredSchedules || []).map(schedule => ({
                  ...schedule,
                  assigned_guards: schedule.assigned_guards || [],
                  color: 'Primary',
                  created_by: user?.id || '',
                  created_at: schedule.created_at || new Date().toISOString(),
                  updated_at: schedule.updated_at || new Date().toISOString()
                })), 
                false
              )}
              onCopyCustomRange={(events, withGuards, mode) => handleCopyCustomRange(events, withGuards, mode)}
              onPasteWeek={() => handleShowPasteOptions(currentDate)}
              onClear={(type) => handleClearOption(type, currentDate, 
                customDateRange?.from ? { from: customDateRange.from, to: customDateRange.to || customDateRange.from } : undefined
              )}
              events={(filteredSchedules || []).map(schedule => ({
                ...schedule,
                assigned_guards: schedule.assigned_guards || [],
                color: 'Primary',
                created_by: user?.id || '',
                created_at: schedule.created_at || new Date().toISOString(),
                updated_at: schedule.updated_at || new Date().toISOString()
              }))}
              hasCopiedShifts={hasCopiedShifts}
              withGuards={withGuards}
              onToggleCustomView={viewMode === 'week' || viewMode === 'custom' ? handleToggleCustomView : undefined}
              isCustomView={isCustomView}
              onDateRangeSelect={handleDateRangeSelectAdapter}
              viewMode={viewMode}
              teamId={teamId}
              isVisible={isCurrentWeekVisible}
              onVisibilityChange={handleWeekVisibilityChange}
              isClearing={isClearing}
              customDateRange={customDateRange}
            />
          )}
          
          {/* Show visibility toggle for managers too */}
          {hasWeekVisibilityPermissions && !hasAdminLikePermissions && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Week Visibility</span>
              <WeekVisibilityToggle
                date={currentDate}
                teamId={teamId}
                isVisible={isCurrentWeekVisible}
                onVisibilityChange={handleWeekVisibilityChange}
              />
            </div>
          )}
          
          {viewMode === 'custom' && (
            <div className="mt-4 mb-2">
              <h3 className="text-sm font-medium mb-2">{t('selectDateRange') || "Select Date Range:"}</h3>
              <CustomDateRangePicker />
            </div>
          )}
        </CardHeader>
        
        <div className="overflow-visible">
          {isLoadingVisibility ? (
            <div className="flex justify-center items-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <CalendarViewSwitcher
              key={`calendar-${guardsVersion}`}
              viewMode={viewMode}
              currentDate={currentDate}
              filteredSchedules={filteredSchedules}
              dateRange={currentDateRange}
              isCustomView={isCustomView}
              isAdmin={hasAdminLikePermissions}
              guards={guards}
              selectedGuardId={selectedGuardId}
              guardSchedules={guardSchedules}
              allGuardsSchedules={allGuardsSchedules}
              isLoadingGuardSchedules={isLoadingGuardSchedules}
              user={user}
              permissions={permissions}
              handleDayClick={handleDayClick}
              handleEventClick={handleEventClickWithQuickEdit}
              onDuplicateEvent={handleDuplicateEvent}
              onDeleteEvent={handleDeleteEvent}
              handleGuardSelect={handleGuardSelect}
              onMarkForSwap={permissions.canUpdate ? markShiftForSwap : undefined}
              onSwapWithMarked={permissions.canUpdate ? (event) => swapShifts(event, () => refetch()) : undefined}
              onCancelSwap={permissions.canUpdate ? clearMarkedShift : undefined}
              markedShiftId={markedShiftForSwap?.id || null}
              onDragEnd={(result: any) => {
                if (result && result.event && result.start && result.end) {
                  handleDragEnd(result.event, result.start, result.end);
                }
              }}
              viewType={viewType}
              calendarZoom={calendarZoom}
            />
          )}
        </div>
      </Card>
      
      {showAddDialog && (
        <AddScheduleDialog 
          isOpen={showAddDialog}
          onClose={handleDialogClose}
          selectedDate={selectedDate}
          teamId={teamId}
          onEventAdded={handleEventAdded}
          event={editEvent}
          createShift={createShift}
          updateShift={updateShift}
        />
      )}

      <QuickEditDialog
        isOpen={showQuickEditDialog}
        onClose={() => {
          setShowQuickEditDialog(false);
          setQuickEditEvent(null);
        }}
        event={quickEditEvent}
        onEventUpdated={handleEventAdded}
        guards={guards}
        updateShift={updateShift}
      />
      
      {permissions.canCreate && (
        <CalendarAddButton 
          onClick={handleAddEvent} 
          disabled={false}
        />
      )}
      
      {isBulkUploadOpen && (
        <BulkUploadShifts
          isOpen={isBulkUploadOpen}
          onClose={closeBulkUpload}
          teamId={teamId}
          onSuccess={handleBulkUploadSuccess}
        />
      )}
      
      <TemplatesDialog
        isOpen={isTemplatesDialogOpen}
        onClose={() => setIsTemplatesDialogOpen(false)}
        teamId={teamId}
        onApplyTemplate={(templateShifts) => handleApplyTemplate(templateShifts, currentDate)}
        currentEvents={schedules as any}
        currentWeekStart={currentWeekStart}
      />
      
      <DeleteShiftConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleCancelDeleteShift}
        onConfirm={handleConfirmDeleteShift}
        shift={shiftToDelete}
        isDeleting={isDeleting}
      />
      
      <PasteOptionsDialog
        isOpen={pasteOptions.isOpen}
        onClose={handlePasteOptionsClose}
        onConfirm={handlePasteOptionsConfirm}
        targetDate={pasteOptions.targetDate || new Date()}
        shiftsCount={pasteOptions.shiftsCount}
        copiedWithGuards={pasteOptions.copiedWithGuards || withGuards}
        copyMode={pasteOptions.copyMode || copyMode}
      />

      <CopyConfirmDialog
        isOpen={copyConfirm?.isOpen || false}
        onClose={() => setCopyConfirm(null)}
        onConfirm={copyConfirm?.onConfirm || (() => {})}
        shiftsCount={copyConfirm?.shiftsCount || 0}
        withGuards={copyConfirm?.withGuards || false}
        copyMode={copyConfirm?.copyMode || 'week'}
        sourceDate={copyConfirm?.sourceDate || new Date()}
        teamName={teams?.find(team => team.id === teamId)?.name || "Team"}
      />

      <PasteConfirmDialog
        isOpen={pasteConfirm?.isOpen || false}
        onClose={() => {
          console.log('ScheduleCalendar: Closing paste confirm dialog');
          setPasteConfirm(null);
        }}
        onConfirm={() => {
          console.log('ScheduleCalendar: Paste confirm dialog onConfirm');
          if (pasteConfirm?.onConfirm) {
            pasteConfirm.onConfirm();
          }
          setPasteConfirm(null);
        }}
        shiftsCount={pasteConfirm?.shiftsCount || 0}
        targetDate={pasteConfirm?.targetDate || new Date()}
        sourceDate={pasteConfirm?.sourceDate || new Date()}
        copyMode={pasteConfirm?.copyMode || 'week'}
        withGuards={pasteConfirm?.withGuards || false}
        includeGuards={pasteConfirm?.includeGuards || false}
      />
    </div>
  );
};

export default ScheduleCalendar;
